import { api_fetch, DeductionExt, DeductionListJson } from "@/api"
import { OrgFieldConfig } from "@/api/org_field_config.tsx"
import { UserState } from "@/auth/user"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ColumnVisibilityMenu } from "@/deduction_table/column_visibility_menu"
import { DEDUCTION_TABLE_SEARCH_PLACEHOLDER, SearchInput } from "@/deduction_table/debounced_input"
import {
  filterDownloadDataBySelectedColumns,
  getAvailableExportColumns,
  buildCsv,
  buildIif,
  collectDownloadData,
  convertBackupToCsv,
  downloadCsv,
  downloadIif,
  GUAYAKI_NETSUITE_CSV_COLUMNS,
  GUAYAKI_ORG_ID,
  mergeGuayakiRtmSplitExportColumns,
  PROMOMASH_CSV_COLUMNS,
  SAFFRON_ROAD_ORG_ID,
} from "@/deduction_table/util"
import { GlobalFilterState } from "@/global_filter"
import { map, toISODateString } from "@/utils"
import { Table } from "@tanstack/react-table"
import { capitalCase } from "change-case"
import { Download, Loader2 } from "lucide-react"
import { ComponentChildren } from "preact"
import { useEffect, useMemo, useRef, useState } from "preact/compat"

interface TableControlsProps {
  table: Table<DeductionListJson>
  orgFieldConfigs?: OrgFieldConfig[]
  enableCsvColumnPicker?: boolean
  csvColumnPickerScope?: string
  /** Show the timestamped validation approval-log export (audit trail of sales-team approvals). */
  showApprovalLogExport?: boolean
  /**
   * Optional async hook for export-time side effects (e.g. Guayaki RTM auto-split) plus a refetch of
   * the currently filtered rows. Callers must return only rows whose ids are in `transactionIds`.
   */
  getExportRows?: (options: { autoSplit: boolean; transactionIds: string[] }) => Promise<DeductionListJson[]>
  /** Extra quick filters rendered next to "Clear Filters" (e.g. the Deductions "Assigned to me" toggle). */
  quickFilters?: ComponentChildren
}

const MAIN_CSV_COLUMNS = [
  "check_date",
  "invoice_date",
  "invoice_number",
  "check_number",
  "customer_name",
  "retailer_name",
  "invoice_amount",
  "category",
  "status_value",
  "portal_link",
  "split_amount",
]

const IMPORTANT_CSV_COLUMNS = [
  ...MAIN_CSV_COLUMNS,
  "kind",
  "execution_date",
  "po_number",
  "reason_code_name",
  "expense_account",
  "account_number",
  "description",
  "remote_accounting_id",
]

const MORE_CSV_COLUMNS = [
  ...IMPORTANT_CSV_COLUMNS,
  "check_amount",
  "source",
  "dc",
  "pending",
  "coded",
  "reason_code_code_description",
  "reason_code_amount",
  "reason_code_execution_date",
  "reason_code_account_number",
  "promotion_remote_ids",
  // Coding detail carried on each GL split (see SPLIT_OVERRIDE_FIELDS in deduction_table/util.tsx).
  "product_line",
  "product_category",
  "promo_type",
  "promo_sub_type",
  "retailer_invoice_number",
]

function getPresetColumns(preset: "main" | "important" | "more", availableColumns: string[]): string[] {
  const target =
    preset === "main" ? MAIN_CSV_COLUMNS : preset === "important" ? IMPORTANT_CSV_COLUMNS : MORE_CSV_COLUMNS
  return target.filter((column: string) => availableColumns.includes(column))
}

function getDefaultCsvExportColumns(
  availableColumns: string[],
  preset: "main" | "important" | "more",
  includeGuayakiRtmColumns: boolean
): string[] {
  const presetColumns = getPresetColumns(preset, availableColumns)
  const baseColumns = presetColumns.length > 0 ? presetColumns : availableColumns
  return includeGuayakiRtmColumns ? mergeGuayakiRtmSplitExportColumns(baseColumns, availableColumns) : baseColumns
}

/**
 * Brands migrating off PromoMash default to the PromoMash "Deduction invoices" column set and order.
 * Returns null for orgs without a brand-specific default (caller falls back to the standard preset).
 */
function getBrandDefaultCsvExportColumns(org_id: string | null | undefined, availableColumns: string[]): string[] | null {
  if (org_id === GUAYAKI_ORG_ID) {
    return GUAYAKI_NETSUITE_CSV_COLUMNS.filter((column: string) => availableColumns.includes(column))
  }
  if (org_id === SAFFRON_ROAD_ORG_ID) {
    return PROMOMASH_CSV_COLUMNS.filter((column: string) => availableColumns.includes(column))
  }
  return null
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("Guayaki NetSuite export defaults", () => {
    it("selects every meaningful reviewed credit-memo column in workbook order", () => {
      const availableColumns = [...GUAYAKI_NETSUITE_CSV_COLUMNS]
      expect(getBrandDefaultCsvExportColumns(GUAYAKI_ORG_ID, availableColumns)).toEqual(availableColumns)
    })
  })
}

export function TableControls({
  table,
  orgFieldConfigs,
  enableCsvColumnPicker = false,
  csvColumnPickerScope = "default",
  showApprovalLogExport = false,
  getExportRows,
  quickFilters,
}: TableControlsProps) {
  let [isDownloadingDeductions, setIsDownloadingDeductions] = useState(false)
  let [isDownloadingApprovalLog, setIsDownloadingApprovalLog] = useState(false)
  let [isDownloadingIif, setIsDownloadingIif] = useState(false)
  let [usesQuickbooksDesktop, setUsesQuickbooksDesktop] = useState(false)
  let [isCsvDialogOpen, setIsCsvDialogOpen] = useState(false)
  let [csvSearch, setCsvSearch] = useState("")
  let [availableCsvColumns, setAvailableCsvColumns] = useState<string[]>([])
  let [selectedCsvColumns, setSelectedCsvColumns] = useState<string[]>([])
  let [selectedCsvPreset, setSelectedCsvPreset] = useState<"main" | "important" | "more" | "all" | "custom">("custom")
  let [preparedCsvData, setPreparedCsvData] = useState<Record<string, any>[]>([])
  let [exportStatusMessage, setExportStatusMessage] = useState<string | null>(null)
  const exportInFlightRef = useRef(false)
  let user = UserState.use(u => u!)
  // Guayaki's audited NetSuite layout gained required columns in August 2026. Version only that
  // brand's saved selection once so an older localStorage choice cannot silently omit them; after
  // the reset, users may customize and persist the new layout normally.
  const csvColumnStorageVersion = user.org_id === GUAYAKI_ORG_ID ? ":netsuite-v2" : ""
  const csvColumnStorageKey = `csv-export-columns:${csvColumnPickerScope}:${user.org_id}${csvColumnStorageVersion}`
  const autoSplitOnExportStorageKey = `auto-split-on-export:${csvColumnPickerScope}:${user.org_id}`
  const [autoSplitOnExport, setAutoSplitOnExport] = useState<boolean>(() => {
    const stored = localStorage.getItem(autoSplitOnExportStorageKey)
    return stored === null ? true : stored === "true"
  })

  const filteredCsvColumns = useMemo(() => {
    const normalizedSearch = csvSearch.trim().toLowerCase()
    if (!normalizedSearch) {
      return availableCsvColumns
    }
    return availableCsvColumns.filter((column: string) => {
      return column.toLowerCase().includes(normalizedSearch) || capitalCase(column).toLowerCase().includes(normalizedSearch)
    })
  }, [csvSearch, availableCsvColumns])

  useEffect(() => {
    async function fetchQuickbooksSetting() {
      const result = await api_fetch<{ uses_quickbooks_desktop: boolean }>("/organization/uses-quickbooks-desktop")
      if (result.ok) {
        setUsesQuickbooksDesktop(result.value.data.uses_quickbooks_desktop)
      }
    }
    fetchQuickbooksSetting()
  }, [])

  function getTableDownloadRows(): DeductionListJson[] {
    return table.getFilteredRowModel().rows.map((row: any) => row.original)
  }

  async function resolveExportRows(options?: { autoSplit?: boolean }): Promise<DeductionListJson[]> {
    const filteredRows = getTableDownloadRows()
    if (!getExportRows || !options?.autoSplit) {
      return filteredRows
    }
    const transactionIds = filteredRows.map((row: DeductionListJson) => row.id)
    return await getExportRows({ autoSplit: true, transactionIds })
  }

  async function getPreparedDownloadData(options?: { autoSplit?: boolean }): Promise<Record<string, any>[]> {
    return collectDownloadData(await resolveExportRows(options), orgFieldConfigs)
  }

  function handleToggleAutoSplitOnExport(checked: boolean) {
    setAutoSplitOnExport(checked)
    localStorage.setItem(autoSplitOnExportStorageKey, String(checked))
  }

  function downloadPreparedCsv(filteredData: Record<string, any>[], selectedColumns?: string[]) {
    const exportData = selectedColumns?.length ? filterDownloadDataBySelectedColumns(filteredData, selectedColumns) : filteredData
    let csv = buildCsv(exportData, user.org_id!, orgFieldConfigs)
    downloadCsv(csv, "output")
  }

  async function openCsvColumnDialog() {
    try {
      setIsDownloadingDeductions(true)
      const filteredData = collectDownloadData(getTableDownloadRows(), orgFieldConfigs)
      const availableColumns = getAvailableExportColumns(filteredData)
      const includeGuayakiRtmColumns = getExportRows !== undefined

      const brandDefaultColumns = getBrandDefaultCsvExportColumns(user.org_id, availableColumns)
      let selectedColumns =
        brandDefaultColumns ?? getDefaultCsvExportColumns(availableColumns, "important", includeGuayakiRtmColumns)
      const storedColumns = localStorage.getItem(csvColumnStorageKey)
      if (storedColumns) {
        try {
          const parsedColumns = JSON.parse(storedColumns) as string[]
          const validStoredColumns = parsedColumns.filter((column: string) => availableColumns.includes(column))
          if (validStoredColumns.length > 0) {
            selectedColumns = validStoredColumns
          }
        } catch (_error) {}
      }

      setPreparedCsvData(filteredData)
      setAvailableCsvColumns(availableColumns)
      setSelectedCsvColumns(selectedColumns)
      setSelectedCsvPreset(brandDefaultColumns ? "custom" : "important")
      setCsvSearch("")
      setIsCsvDialogOpen(true)
    } finally {
      setIsDownloadingDeductions(false)
    }
  }

  function applyCsvPreset(preset: "main" | "important" | "more") {
    setSelectedCsvPreset(preset)
    setSelectedCsvColumns(getDefaultCsvExportColumns(availableCsvColumns, preset, getExportRows !== undefined))
  }

  function toggleCsvColumn(column: string, checked: boolean) {
    setSelectedCsvPreset("custom")
    setSelectedCsvColumns((current: string[]) => {
      if (checked) {
        return current.includes(column) ? current : [...current, column]
      }
      return current.filter((value: string) => value !== column)
    })
  }

  async function handleExportCsvClick() {
    if (enableCsvColumnPicker) {
      await openCsvColumnDialog()
      return
    }
    await downloadDeductionsCsv()
  }

  async function downloadDeductionsCsv() {
    try {
      setIsDownloadingDeductions(true)
      const filteredData = await getPreparedDownloadData()
      downloadPreparedCsv(filteredData)
    } finally {
      setIsDownloadingDeductions(false)
    }
  }

  async function downloadDeductionsCsvWithSelectedColumns() {
    if (exportInFlightRef.current) {
      return
    }
    exportInFlightRef.current = true
    try {
      setIsDownloadingDeductions(true)
      if (getExportRows && autoSplitOnExport) {
        setExportStatusMessage("Running RTM split…")
      }
      localStorage.setItem(csvColumnStorageKey, JSON.stringify(selectedCsvColumns))
      const exportData = autoSplitOnExport
        ? await getPreparedDownloadData({ autoSplit: true })
        : preparedCsvData
      downloadPreparedCsv(exportData, selectedCsvColumns)
      setIsCsvDialogOpen(false)
    } finally {
      exportInFlightRef.current = false
      setExportStatusMessage(null)
      setIsDownloadingDeductions(false)
    }
  }

  const { startDate: globalStartDate, endDate: globalEndDate, dateKind: globalDateKind } = GlobalFilterState.use()

  /**
   * Download the server-built approval log: per validated deduction, the task assigned to the
   * sales team and the validated-by / validated-at audit trail, scoped to the global date filter.
   */
  async function downloadApprovalLog() {
    try {
      setIsDownloadingApprovalLog(true)
      const params = new URLSearchParams()
      const start = map(globalStartDate, toISODateString)
      const end = map(globalEndDate, toISODateString)
      if (start) params.set("start_date", start)
      if (end) params.set("end_date", end)
      if (globalDateKind) params.set("date_kind", globalDateKind)
      const response = await fetch(`/api/deduction/validation-audit-export?${params}`, {
        credentials: "include",
      })
      if (!response.ok) {
        throw new Error("Approval log export failed")
      }
      downloadCsv(await response.text(), "approval_log")
    } finally {
      setIsDownloadingApprovalLog(false)
    }
  }

  async function downloadDeductionsIif() {
    try {
      setIsDownloadingIif(true)
      let dataToDownload = table.getFilteredRowModel().rows.map(row => row.original)
      let iif = buildIif(collectDownloadData(dataToDownload, orgFieldConfigs))
      downloadIif(iif, "output")
    } finally {
      setIsDownloadingIif(false)
    }
  }

  return (
    <div className="flex items-center justify-between py-4">
      <div className="flex w-full space-x-2">
        <div className="flex space-x-2">
          {/*<TransactionTypeFilter className="" />*/}
          <Button variant="outline" className="text-slate-500" onClick={(table.options.meta as any).clearAllFilters}>
            Clear Filters
          </Button>
          {quickFilters}
        </div>
        <SearchInput
          value={table.getState().globalFilter ?? ""}
          onChange={value => table.setGlobalFilter(String(value))}
          className="w-full p-2 font-lg"
          placeholder={DEDUCTION_TABLE_SEARCH_PLACEHOLDER}
        />
        {/*<Button className="text-slate-500" onClick={downloadBackupCsv} variant="outline" disabled={isDownloadingBackup}>
          {isDownloadingBackup ? <Loader2 className="h-4 animate-spin" /> : <Download className="h-4" />}
          Backup
        </Button>*/}
        <div class="flex justify-end mr-3 space-x-2">
          <Button
            className="text-slate-500"
            onClick={handleExportCsvClick}
            variant="outline"
            disabled={isDownloadingDeductions}>
            {isDownloadingDeductions ? <Loader2 className="h-4 animate-spin" /> : <Download className="-ml-1 h-4" />}
            Export CSV
          </Button>
          {showApprovalLogExport && (
            <Button
              className="text-slate-500"
              onClick={downloadApprovalLog}
              variant="outline"
              disabled={isDownloadingApprovalLog}>
              {isDownloadingApprovalLog ? <Loader2 className="h-4 animate-spin" /> : <Download className="-ml-1 h-4" />}
              Approval Log
            </Button>
          )}
          {usesQuickbooksDesktop && (
            <Button
              className="text-slate-500"
              onClick={downloadDeductionsIif}
              variant="outline"
              disabled={isDownloadingIif}>
              {isDownloadingIif ? <Loader2 className="h-4 animate-spin" /> : <Download className="-ml-1 h-4" />}
              Export IIF
            </Button>
          )}
        </div>
        <ColumnVisibilityMenu table={table} />
      </div>
      <Dialog open={isCsvDialogOpen} onOpenChange={setIsCsvDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select CSV columns</DialogTitle>
            <DialogDescription>
              Choose which columns to include in your export. Quick actions help you start with common column sets.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 min-h-0">
            <div className="flex flex-wrap gap-2">
              <Button variant={selectedCsvPreset === "main" ? "secondary" : "outline"} onClick={() => applyCsvPreset("main")}>
                Main columns
              </Button>
              <Button
                variant={selectedCsvPreset === "important" ? "secondary" : "outline"}
                onClick={() => applyCsvPreset("important")}>
                Most important
              </Button>
              <Button variant={selectedCsvPreset === "more" ? "secondary" : "outline"} onClick={() => applyCsvPreset("more")}>
                More columns
              </Button>
              <Button
                variant={selectedCsvPreset === "all" ? "secondary" : "ghost"}
                onClick={() => {
                  setSelectedCsvPreset("all")
                  setSelectedCsvColumns(availableCsvColumns)
                }}>
                Select all
              </Button>
              <Button
                variant={selectedCsvPreset === "custom" && selectedCsvColumns.length === 0 ? "secondary" : "ghost"}
                onClick={() => {
                  setSelectedCsvPreset("custom")
                  setSelectedCsvColumns([])
                }}>
                Clear
              </Button>
            </div>
            <Input
              value={csvSearch}
              onChange={event => setCsvSearch(event.currentTarget.value)}
              placeholder="Search columns..."
            />
            <div className="text-xs text-slate-500">
              {selectedCsvColumns.length} selected of {availableCsvColumns.length}
            </div>
            <div className="overflow-y-auto border rounded-md p-2 max-h-[45vh]">
              {filteredCsvColumns.length > 0 ? (
                filteredCsvColumns.map((column: string) => {
                  const checked = selectedCsvColumns.includes(column)
                  return (
                    <div key={column} className="flex items-center gap-2 py-1">
                      <Checkbox checked={checked} onCheckedChange={value => toggleCsvColumn(column, !!value)} />
                      <Label className="cursor-pointer" onClick={() => toggleCsvColumn(column, !checked)}>
                        {capitalCase(column)}
                      </Label>
                    </div>
                  )
                })
              ) : (
                <div className="text-sm text-slate-500 py-3">No matching columns.</div>
              )}
            </div>
          </div>
          {getExportRows && (
            <div className="flex items-center gap-2 pt-2 border-t">
              <Checkbox
                id="auto-split-on-export"
                checked={autoSplitOnExport}
                onCheckedChange={(value: boolean | "indeterminate") => handleToggleAutoSplitOnExport(!!value)}
              />
              <Label htmlFor="auto-split-on-export" className="cursor-pointer text-sm whitespace-nowrap">
                Auto-split RTM on export
              </Label>
            </div>
          )}
          <DialogFooter>
            {exportStatusMessage && (
              <span className="text-sm text-slate-500 mr-auto flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {exportStatusMessage}
              </span>
            )}
            <Button variant="outline" onClick={() => setIsCsvDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={downloadDeductionsCsvWithSelectedColumns}
              disabled={selectedCsvColumns.length === 0 || isDownloadingDeductions}>
              {isDownloadingDeductions ? <Loader2 className="h-4 animate-spin" /> : <Download className="-ml-1 h-4" />}
              Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
